





































import Vue from "vue";
import {
  DrillQuestionAndAnswers,
  DrillListeningAnswer,
  ApiDrillListening,
  DrillLearningStatus,
  RespDrillListeningAnswer
} from "@tt-app/models/exercise";

import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

import IconCorrect from "@joyz/app-core/assets/icons/Correct.svg";
import IconIncorrect from "@joyz/app-core/assets/icons/Incorrect.svg";
import IconSpeaker from "@joyz/app-core/assets/icons/white/Speaker.svg";
import { MCQOptions } from "@joyz/app-core/models/drill";
import { audioContext } from "@tt-app/utils/audio/audioContext";

interface Data {
  answerSelected: boolean;
  icons: { [key: string]: string };
  // The user cannot select an answer if the audio has not been listened once
  isAudioListened: boolean;
}

interface Methods {
  onAnswerSelected: () => void;
  playWordAudio: () => void;
  submit: (option: MCQOptions) => void;
}

interface Computed {
  canSelect: boolean;
  drillQuestion: ApiDrillListening;
  learnStatus: number;
  learnStatusDecrease: boolean | undefined;
  learnStatusIncrease: boolean | undefined;
  questionOptions: MCQOptions[];
  questionCorrectAnswerId: number | undefined;
  questionSelectedId: number | undefined;
}

interface Props {
  audioDecoderPlayer: AudioDecoderPlayer;
  drillExercise: DrillQuestionAndAnswers;
  isFinished: boolean;
  isLoading: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "DrillQuestionListening",
  components: {},

  props: {
    audioDecoderPlayer: { type: Object as () => AudioDecoderPlayer, required: true },
    drillExercise: { type: Object as () => DrillQuestionAndAnswers, required: true },
    isFinished: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false }
  },

  data() {
    return {
      answerSelected: false,
      icons: {
        correct: IconCorrect,
        incorrect: IconIncorrect,
        speaker: IconSpeaker
      },
      isAudioListened: false
    };
  },

  computed: {
    canSelect() {
      return !this.isFinished && !this.drillExercise.lastAnswer && this.isAudioListened;
    },

    drillQuestion() {
      return this.drillExercise.question as ApiDrillListening;
    },

    learnStatus() {
      const getStatus = (status: DrillLearningStatus): number => (status !== null ? status : -1);

      return this.drillExercise.lastAnswer
        ? getStatus(this.drillExercise.lastAnswer.learnStatus.newLearnStatus)
        : getStatus(this.drillExercise.question.learnedStatus);
    },

    learnStatusDecrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === false
        : false;
    },

    learnStatusIncrease() {
      return this.drillExercise.lastAnswer
        ? this.drillExercise.lastAnswer.learnStatus.hasIncreased === true
        : false;
    },

    questionOptions() {
      return this.drillQuestion.options.map((o): MCQOptions => ({ id: o.id, text: o.text }));
    },

    questionCorrectAnswerId() {
      if (this.drillExercise.skipped) {
        const correctId = (this.drillExercise.skipped as RespDrillListeningAnswer).correctId;
        return correctId;
      }

      if (this.drillExercise.lastAnswer) {
        const correction = this.drillExercise.lastAnswer.correction as RespDrillListeningAnswer;
        return correction.correctId;
      }

      return undefined;
    },

    questionSelectedId() {
      if (this.drillExercise.lastAnswer) {
        const submittedAnswer = this.drillExercise.lastAnswer
          .submittedAnswer as DrillListeningAnswer;
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.answerSelected = false;
        return submittedAnswer.optionId;
      }

      return undefined;
    }
  },

  watch: {
    "drillExercise.question.uuid": {
      handler(newVal: string, oldVal: string) {
        if (newVal !== oldVal) {
          this.isAudioListened = false;

          const mainContainer = document.querySelector(".drill-main-container");
          if (mainContainer) {
            mainContainer.scrollTo(0, 0);
          }
        }
      },
      immediate: false
    }
  },

  methods: {
    async playWordAudio() {
      if (this.audioDecoderPlayer.status === "playing") {
        this.audioDecoderPlayer.stop();
      }

      await audioContext.resume();
      await this.audioDecoderPlayer.play(this.drillQuestion.word.audio);
      this.isAudioListened = true;
    },

    onAnswerSelected() {
      this.answerSelected = true;
    },

    async submit(option) {
      // User already answered, cannot retry here
      if (!this.canSelect) {
        return;
      }

      const answer: DrillListeningAnswer = {
        type: "DRILL_LISTENING",
        optionId: option.id as number,
        uuid: this.drillExercise.question.uuid
      };
      this.$emit("on-user-answer", answer);
      await this.$nextTick();
    }
  }
});
